// Keeps the access token consistent across tabs.
//
// All tabs share one refresh cookie, and every refresh revokes the refresh token it used
// and the access token issued with it. So two tabs refreshing at once means one of them
// presents a revoked refresh token and is logged out, and any refresh leaves the other tabs
// holding a revoked access token. To avoid both, tabs refresh one at a time and share the
// result through localStorage.

const ACCESS_TOKEN_KEY = 'accessToken';
const EXPIRATION_KEY = 'expirationTime';
const REFRESH_LOCK = 'myarcticspa-token-refresh';
// A token another tab fetched is only worth adopting if it isn't about to expire itself.
const MIN_REMAINING_MS = 60_000;
// Longer than the refresh request's own timeout, so a healthy holder always finishes first.
const LOCK_WAIT_MS = 20_000;

export interface SharedToken {
    accessToken: string;
    expirationTime: number;
}

export const readSharedToken = (): SharedToken | null => {
    try {
        const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
        const expirationTime = Number(localStorage.getItem(EXPIRATION_KEY));
        if (!accessToken || !expirationTime) return null;
        return { accessToken, expirationTime };
    } catch {
        return null;
    }
};

export const writeSharedToken = ({ accessToken, expirationTime }: SharedToken) => {
    try {
        // Written in this order so the `expirationTime` event means both are in place.
        localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
        localStorage.setItem(EXPIRATION_KEY, Math.floor(expirationTime).toString());
    } catch {
        // Storage unavailable; this tab still works on its own.
    }
};

/**
 * Refresh the token, unless another tab already did while this one waited its turn.
 *
 * @param staleToken The token this tab had when it decided to refresh.
 * @param refresh Calls the refresh API and returns the new token.
 */
export const refreshWithLock = async (
    staleToken: string | null,
    refresh: () => Promise<SharedToken>
): Promise<SharedToken> => {
    const run = async () => {
        const shared = readSharedToken();
        if (
            shared &&
            shared.accessToken !== staleToken &&
            shared.expirationTime - Date.now() > MIN_REMAINING_MS
        ) {
            return shared;
        }
        const fresh = await refresh();
        // Publish before releasing the lock, so the next tab in line sees it.
        writeSharedToken(fresh);
        return fresh;
    };

    // Web Locks are shared by every tab on this origin.
    if (typeof navigator === 'undefined' || !navigator.locks?.request) {
        return run();
    }

    // Never wait on another tab indefinitely. If the lock doesn't free up, refresh without
    // it: a rare race beats a tab stuck on a spinner.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), LOCK_WAIT_MS);
    try {
        return await navigator.locks.request(REFRESH_LOCK, { signal: controller.signal }, () => {
            clearTimeout(timeoutId);
            return run();
        });
    } catch (error: any) {
        if (error?.name !== 'AbortError') throw error;
        console.warn('[tokenSync] Timed out waiting for another tab to refresh, refreshing anyway');
        return run();
    } finally {
        clearTimeout(timeoutId);
    }
};

/** Mirror logins, refreshes and logouts from other tabs. Returns the unsubscribe function. */
export const listenForTokenChanges = (
    onToken: (token: SharedToken) => void,
    onLogout: () => void
): (() => void) => {
    const handleStorage = (event: StorageEvent) => {
        const tokenRemoved = event.key === ACCESS_TOKEN_KEY && event.newValue === null;
        // `expirationTime` is written last, so both values are in place by its event.
        // A `null` key means another tab cleared all of localStorage.
        if (!tokenRemoved && event.key !== EXPIRATION_KEY && event.key !== null) return;

        const shared = readSharedToken();
        if (shared) {
            onToken(shared);
        } else {
            // Logged out elsewhere.
            onLogout();
        }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
};
